import { useState, useCallback } from 'react';
import { ProcessedData, TableData,AjaxOptions, ObjectMetadata } from './OCVTypes';
import { useFetchDynamicSectionMutation } from '../../Redux/OCV/OCVSlice';

export const useOCVDataProcessor = () => {
  const [processedData, setProcessedData] = useState<ProcessedData>({
    fixed: [],
    moving: [],
    metadata: {},
    exportData: []
  });
  const [fetchDynamicSection] = useFetchDynamicSectionMutation();

  const processDynamicSection = useCallback(async (
    metadata: ObjectMetadata,
    tableId: string
  ) => {
   
    if (!metadata?.ajaxOpts) return null;
  

    const ajaxOpts: AjaxOptions = {
      type: "GET",
      cache: false,
      dataType: "json",
      ...metadata.ajaxOpts,
      url: metadata.ajaxOpts.url.replace('$baseURL', window.baseURL)
    };
    try {
      const requestParams: any = {
        url: ajaxOpts.url,
        method: ajaxOpts.type || 'GET',
        contentType: ajaxOpts.contentType
      };
  
      // Only add data if it exists in ajaxOpts
      if (ajaxOpts.data && Object.keys(ajaxOpts.data).length > 0) {
        if (requestParams.method.toUpperCase() === 'GET') {
          // For GET requests, append data as query parameters
          const searchParams = new URLSearchParams(ajaxOpts.data).toString();
          requestParams.url = `${requestParams.url}${searchParams ? '?' + searchParams : ''}`;
        } else {
          // For other methods, add data as body
          requestParams.data = ajaxOpts.data;
        }
      }
      const response = await fetchDynamicSection(requestParams).unwrap();
  
      return response;
    } catch (error) {
      console.error('Error fetching dynamic section:', error);
      throw error;
    }
  }, [fetchDynamicSection]);

  const processObjectData = useCallback((
    objectData: Record<string, any>,
    ocvType: string,
    launcherMode: string = 'popup'
  ): ProcessedData | null =>  {
    // Check if report exists
    const reportExists = objectData["Search Details for Report"] 
      ? objectData["Search Details for Report"].Item2.length > 0 
      : true;

    if (!reportExists) {
      return null;
    }

    const result: ProcessedData = {
      fixed: [],
      moving: [],
      metadata: {},
      exportData: []
    };

    // Process metadata first
    const metadataOfTables = objectData["MetaData"];
    let fixedSectionNotApplicable = true;
    let allTableMetadatas: Record<string, any> = {};

    if (metadataOfTables?.Item2) {
      metadataOfTables.Item2.forEach(([tableKey, value]: [string, string]) => {
        try {
          const tableSpecificMetadata = JSON.parse(value);
          allTableMetadatas[tableKey] = tableSpecificMetadata;
          result.metadata[tableKey] = tableSpecificMetadata;
          if (tableSpecificMetadata.fixedSection) {
            fixedSectionNotApplicable = false;
          }
        
        } catch (e) {
          console.error(`Error parsing metadata for key ${tableKey}:`, e);
        }
      });
    }

    // Process tables
    let fixedDivEnabled = false;
    let movingDivAdded = false;
    let tableId = 0;

    Object.entries(objectData).forEach(([tableKey, tableData]: [string, any]) => {
      if (tableKey !== "MetaData" && !tableData.Item3?.[0]) {// Skip if hidden
        const tableSpecificMetadata = allTableMetadatas[tableKey] || {};
        const table: TableData = {
          key: tableKey,
          ...tableData,
          tableHeading: tableKey,
          metadata: tableSpecificMetadata,
          isDynamic: !!tableSpecificMetadata.ajaxOpts
        };

        if (tableSpecificMetadata.ajaxOpts) {
          table.ajaxOptions = {
            ...tableSpecificMetadata.ajaxOpts,
            url: tableSpecificMetadata.ajaxOpts.url.replace('$baseURL', window.baseURL)
          };
        }
        
        
          // Handle section transitions
         if (fixedSectionNotApplicable && !movingDivAdded && 
            launcherMode !== "GenericFlat" && launcherMode !== "flat") {
          movingDivAdded = true;
        }

        if (fixedDivEnabled && !tableSpecificMetadata.fixedSection && 
            launcherMode !== "GenericFlat" && launcherMode !== "flat") {
          table.needsSeparator = true;
          fixedDivEnabled = false;
        }

        

           // Determine section placement
           if (tableSpecificMetadata.fixedSection) {
            result.fixed.push(table);
            fixedDivEnabled = true;
          } else {
            result.moving.push(table);
          }

        // Store for export
        result.exportData.push(table);
        tableId++;
      }
    });

    setProcessedData(result);
    return result;
  }, []);

  return { processedData, processObjectData,processDynamicSection  };
};